<template>
  <div class="pb-18">
    <div class="md:sticky md:top-0 py-3 border-b -mt-px bg-white dark:bg-gray-900 z-10 overflow-x-auto">
      <div class="container flex justify-between gap-x-8">
        <tabs :tabs="tabItems"/>

        <div class="flex gap-3">
          <share-button
            v-slot="{ openModal }"
            title="Share comparison"
            class="flex items-center"
          >
            <l-button
              v-if="breakpoints.smAndUp"
              variant="light"
              @click="openModal"
            >
              <icon name="share"/>
              Share
            </l-button>
            <l-button
              v-else
              aria-label="Share"
              class="text-gray-500 py-1 px-2 rounded hover:bg-gray-400/10"
              @click="openModal"
            >
              <icon name="share" class="text-sm"/>
            </l-button>
          </share-button>

          <div
            class="flex justify-center fixed z-10 m-auto inset-x-0 bottom-4 md:justify-end md:static sm:m-0"
          >
            <compare-school-modal v-slot="{ openModal }" :model-value="schools">
              <l-button
                variant="primary"
                @click="openModal"
              >
                Select schools
              </l-button>
            </compare-school-modal>
          </div>
        </div>
      </div>
    </div>

    <h2 class="sr-only">
      Overview
    </h2>

    <compare-section id="overview" :class="[classes.section, '!pt-8 !md:pt-10']">
      <compare-row v-if="desktop" v-slot="{ school }" v-bind="{ schools }" :class="[classes.horizontal, 'mb-8 md:mb-10']">
        <school-card
          class="border border-gray-300 dark:border-gray-800 shadow-sm rounded-lg p-4"
          :school="school"
        />

        <div class="flex flex-wrap justify-center text-center gap-2 mt-4">
          <l-button variant="secondary" size="sm" :href="route('schools.show', school)">
            View full profile
          </l-button>
          <l-button
            variant="light"
            size="sm"
            :aria-label="`Remove ${ school.name } from comparison list`"
            :href="route('compare', schools.filter((s) => s.slug !== school.slug).map((s) => s.slug).join('-vs-'))"
          >
            Remove
          </l-button>
        </div>
      </compare-row>

      <compare-row v-else v-slot="{ school }" v-bind="{ schools }" :class="classes.horizontal">
        <div class="font-medium text-blue dark:text-white border-b flex justify-between items-center leading-none pb-1">
          {{ school.name_limited }}

          <l-button class="text-gray-500 text-sm p-2" :href="route('compare', schools.filter((s) => s.slug !== school.slug).map((s) => s.slug).join('-vs-'))">
            <icon name="remove"/>
          </l-button>
        </div>
      </compare-row>

      <compare-row v-slot="{ school }" v-bind="{ schools }" :class="classes.horizontal">
        <compare-stat>
          <template #value>
            {{ school.latest_admissions.enrollees_All }}
          </template>
          New students <br class="md:hidden"> in {{ school.latest_admissions.admission_year }}
        </compare-stat>
      </compare-row>

      <compare-row v-slot="{ school }" v-bind="{ schools }" :class="classes.horizontal">
        <compare-stat>
          <template #value>
            {{ school.latest_employment_aba.grads }}
          </template>
          Graduates <br class="md:hidden"> in  {{ school.latest_employment_aba.graduation_year }}
        </compare-stat>
      </compare-row>

      <compare-row v-slot="{ school }" v-bind="{ schools }" :class="classes.horizontal">
        <compare-stat v-if="school.tuition_tiers?.length">
          <template #value>
            {{ currency(school.tuition_tiers[0].tuition) || 'Not reported' }}
          </template>
          {{ school.tuition_tiers[0].title }} <br class="md:hidden"> in
          {{ school.tuition_tiers[0].academic_year }}-{{ school.tuition_tiers[0].academic_year - 1999 }} AY
        </compare-stat>
      </compare-row>

      <compare-row v-slot="{ school }" v-bind="{ schools }" :class="classes.horizontal">
        <compare-stat>
          <template #value>
            {{ currency(school.cost_of_living) || 'Not reported' }}
          </template>
          Cost of living <br class="md:hidden"> in {{ school.address }}
          <term
            v-if="desktop"
            name="costOfLiving"
            pos="left"
          />
        </compare-stat>
      </compare-row>

      <compare-row v-slot="{ school, index }" v-bind="{ schools }" :class="classes.horizontal">
        <compare-stat>
          <template #value>
            {{ currency(school.median_earnings) || 'Not reported' }}
          </template>
          Median earnings
          <term
            v-if="desktop"
            name="medianEarnings"
            :pos="index == 0 ? 'up-left' : 'up-right'"
            :href="route('trends.costs', { page: 'debt-income', scope: 'schools' })"
          />
        </compare-stat>
      </compare-row>

      <compare-row v-slot="{ school }" v-bind="{ schools }" :class="classes.horizontal">
        <compare-stat>
          <div class="inline-flex items-center gap-1">
            <template v-if="school.latest_prices_pt">
              <icon name="squareCheck" class="text-green"/> has part-time program
            </template>

            <template v-else>
              <icon name="squareX" class="text-red"/> no part-time program
            </template>
          </div>
        </compare-stat>
      </compare-row>
    </compare-section>

    <compare-section id="jobs" :class="classes.section" title="Top states by job placement">
      <compare-row v-slot="{ school, index }" v-bind="{ schools }" :class="classes.vertical">
        <top-states-card
          v-if="school.top_employment_location"
          class="h-full"
          help=""
          :color="schoolColors[index]"
          :level="3"
          :school="school"
          :title="school.name_limited"
        />
      </compare-row>
    </compare-section>

    <compare-section :class="classes.section" title="Jobs by employer type">
      <compare-row v-slot="{ school, index }" v-bind="{ schools }" :class="classes.vertical">
        <job-outcomes-card
          v-if="school.jobs_by_employer_type_flat"
          help=""
          :color="schoolColors[index]"
          :level="3"
          :school="school"
          :title="school.name_limited"
        />
      </compare-row>
    </compare-section>

    <template v-if="desktop">
      <compare-section :class="classes.section" title="Employment Score" term="employmentScore">
        <compare-row v-slot="{ school }" v-bind="{ schools }" :class="classes.vertical">
          <sparkline-employment-score
            help=""
            size="lg"
            :level="3"
            :school="school"
            :title="school.name_limited"
          />
        </compare-row>
      </compare-section>

      <compare-section :class="classes.section" title="Underemployment Score" term="underemploymentScore">
        <compare-row v-slot="{ school }" v-bind="{ schools }" :class="classes.vertical">
          <sparkline-underemployment-score
            help=""
            size="lg"
            :level="3"
            :school="school"
            :title="school.name_limited"
          />
        </compare-row>
      </compare-section>
    </template>

    <compare-section v-else :class="classes.section" title="Scores">
      <card>
        <template #header>
          <h2 class="font-medium text-blue dark:text-white">
            Employment Score <term name="employmentScore" pos="down"/>
          </h2>
        </template>
        <template #body>
          <div class="text-sm space-y-3">
            <percent-bar
              v-for="(school, index) in schools"
              :key="school.id"
              :item="school"
              :color="schoolColors[index]"
              :label="school.name_limited"
              :percent="school.employment_scores[school.employment_scores.length - 1].score"
            />
          </div>
        </template>
      </card>
      <card>
        <template #header>
          <h2 class="font-medium text-blue dark:text-white">
            Underemployment Score <term name="underemploymentScore"/>
          </h2>
        </template>
        <template #body>
          <div class="text-sm space-y-3">
            <percent-bar
              v-for="(school, index) in schools"
              :key="school.id"
              :label="school.name_limited"
              :color="schoolColors[index]"
              :percent="school.underemployment_scores[school.underemployment_scores.length - 1].score"
            />
          </div>
        </template>
      </card>
    </compare-section>

    <compare-section
      id="admissions"
      :class="classes.section"
      :title="user?.lsat && user?.gpa ? 'Admission chances': 'Admissions profile'"
      :term="user?.lsat && user?.gpa ? 'admissionChances': null"
    >
      <slot v-bind="{ classes, schools }" :class="classes.vertical"/>
    </compare-section>

    <compare-section :class="classes.section" title="1L enrollment">
      <compare-row v-if="desktop" v-slot="{ school }" v-bind="{ schools }" :class="classes.horizontal">
        <sparkline-enrollment
          help=""
          size="lg"
          :level="3"
          :school="school"
          :title="school.name_limited"
        />
      </compare-row>
      <div v-else class="flex gap-5">
        <div v-for="school in schools" :key="school.id" class="text-center">
          <div class="font-medium text-blue dark:text-white mb-5">
            {{ school.name_limited }}
          </div>
          <sparkline-enrollment
            size="sm"
            :level="3"
            :school="school"
            :title="school.name_limited"
          />
        </div>
      </div>
    </compare-section>

    <compare-section id="environment" :class="classes.section" title="First-time bar pass rates">
      <compare-row v-if="desktop" v-slot="{ school }" v-bind="{ schools }" :class="classes.horizontal">
        <sparkline-bar-pass-rates
          help=""
          size="lg"
          :level="3"
          :school="school"
          :title="school.name_limited"
        />
      </compare-row>
      <div v-else class="flex gap-5">
        <div v-for="school in schools" :key="school.id" class="text-center">
          <div class="font-medium text-blue dark:text-white mb-5">
            {{ school.name_limited }}
          </div>
          <sparkline-bar-pass-rates
            help=""
            size="sm"
            :level="3"
            :school="school"
            :title="school.name_limited"
          />
        </div>
      </div>
    </compare-section>

    <compare-section v-if="desktop" :class="classes.section" title="Diversity">
      <compare-row v-slot="{ school }" v-bind="{ schools }" :class="classes.vertical">
        <card
          help=""
          :href="route('schools.environment', school)"
          :level="3"
          :title="school.name_limited"
        >
          <template #body>
            <div class="grid gap-2">
              <template v-if="school.women_enrollment_percent || school.poc_enrollment_percent">
                <div v-if="school.women_enrollment_percent >= 0">
                  <span class="font-semibold text-blue dark:text-white">{{ school.women_enrollment_percent }}%</span>
                  women enrolled
                </div>
                <div v-if="school.poc_enrollment_percent >= 0">
                  <span class="font-semibold text-blue dark:text-white">{{ school.poc_enrollment_percent }}%</span>
                  students of color enrolled
                </div>
              </template>
              <div v-else>
                Missing data
              </div>
            </div>
          </template>
        </card>
      </compare-row>
    </compare-section>

    <compare-section v-else :class="classes.section" title="Diversity">
      <card>
        <template #header>
          <h3 class="font-medium text-blue dark:text-white">
            Women enrolled
          </h3>
        </template>

        <template #body>
          <div class="grid gap-3 text-sm">
            <percent-bar
              v-for="(school, index) in schools"
              :key="school.id"
              :label="school.name_limited"
              :percent="school.women_enrollment_percent"
              :color="schoolColors[index]"
            />
          </div>
        </template>
      </card>
      <card>
        <template #header>
          <h3 class="font-medium text-blue dark:text-white">
            Students of color enrolled
          </h3>
        </template>

        <template #body>
          <div class="grid gap-3 text-sm">
            <percent-bar
              v-for="(school, index) in schools"
              :key="school.id"
              :label="school.name_limited"
              :percent="school.poc_enrollment_percent"
              :color="schoolColors[index]"
            />
          </div>
        </template>
      </card>
    </compare-section>

    <compare-section :class="classes.section" title="1L attrition" term="attrition">
      <compare-row v-slot="{ school }" v-bind="{ schools }" :class="classes.vertical">
        <card
          help=""
          :href="route('schools.environment', school)"
          :level="3"
          :title="school.name_limited"
        >
          <template #body>
            <div class="grid gap-2">
              <template v-if="school.attrition_categories">
                <div>
                  <span class="font-semibold text-blue dark:text-white">{{ school.attrition_categories.dismissal }}%</span>
                  academically dismissed
                </div>
                <div>
                  <span class="font-semibold text-blue dark:text-white">{{ school.attrition_categories.dropouts }}%</span>
                  dropped out
                </div>
                <div>
                  <span class="font-semibold text-blue dark:text-white">{{ school.attrition_categories.transfers_out }}%</span>
                  transferred out
                </div>
                <div class="text-xs text-gray-500 text-right">
                  after
                  {{ school.latest_attrition.academic_year }}-{{ (school.latest_attrition.academic_year - 1999) }}
                  AY
                </div>
              </template>
              <div v-else>
                Missing data
              </div>
            </div>
          </template>
        </card>
      </compare-row>
    </compare-section>
  </div>
</template>

<script setup>
import Card from "@/components/cards/Card.vue"
import CompareRow from "@/components/compare/CompareRow.vue"
import CompareSchoolModal from "@/components/modals/CompareSchoolModal.vue"
import CompareSection from "@/components/compare/CompareSection.vue"
import CompareStat from "@/components/compare/CompareStat.vue"
import Icon from "@/components/Icon.vue"
import JobOutcomesCard from "@/components/cards/JobOutcomesCard.vue"
import LButton from "@/components/buttons/LButton.vue"
import PercentBar from "@/components/PercentBar.vue"
import SchoolCard from "@/components/cards/SchoolCard.vue"
import ShareButton from "@/components/buttons/ShareButton.vue"
import SparklineBarPassRates from "@/components/sparklines/SparklineBarPassRates.vue"
import SparklineEmploymentScore from "@/components/sparklines/SparklineEmploymentScore.vue"
import SparklineEnrollment from "@/components/sparklines/SparklineEnrollment.vue"
import SparklineUnderemploymentScore from "@/components/sparklines/SparklineUnderemploymentScore.vue"
import Tabs from "@/components/Tabs.vue"
import Term from "@/components/Term.vue"
import TopStatesCard from "@/components/cards/TopStatesCard.vue"
import colors from "@/colors"
import route from "ziggy-js"
import { breakpoints } from "@/breakpoints"
import { computed } from "vue"
import { currency } from "@/support"
import { useAuth } from "@/composables/useAuth"
const props = defineProps({
  schools: { type: Array, required: true },
})

const { user } = useAuth()

const tabItems = [
  { id: "overview", title: "Overview" },
  { id: "jobs", title: "Jobs" },
  { id: "admissions", title: "Admissions" },
  { id: "environment", title: "Environment" },
]

const desktop = computed(() => {
  return breakpoints.mdAndUp
})

const schoolColors = computed(() => (desktop.value
  ? [colors.blue[400], colors.blue[400], colors.blue[400], colors.blue[400]]
  : [colors.blue[400], colors.orange[400], colors.teal[400], colors.rose[400]]
))

const classes = computed(() => ({
  horizontal: [
    "grid gap-5 lg:gap-8",
    {
      "gap-x-3 grid-cols-[repeat(1,45%)]": props.schools.length == 1,
      "gap-x-3 grid-cols-2 md:grid-cols-[repeat(2,22rem)] lg:grid-cols-[repeat(2,28rem)]": props.schools.length == 2,
      "gap-x-3 grid-cols-[repeat(3,45%)] md:grid-cols-[repeat(3,18rem)]": props.schools.length == 3,
      "gap-x-3 grid-cols-[repeat(4,45%)] md:grid-cols-4": props.schools.length == 4,
    },
  ],
  vertical: [
    "grid gap-5 lg:gap-8",
    {
      "sm:grid-cols-[repeat(1,45%)]": props.schools.length == 1,
      "sm:grid-cols-2 md:grid-cols-[repeat(2,22rem)] lg:grid-cols-[repeat(2,28rem)]": props.schools.length == 2,
      "sm:grid-cols-[repeat(3,45%)] md:grid-cols-[repeat(3,18rem)]": props.schools.length == 3,
      "sm:grid-cols-[repeat(4,45%)] md:grid-cols-4": props.schools.length == 4,
    },
  ],
  section: props.schools.length == 4 ? "" : "md:max-w-max",
}))
</script>
